/**
 * DayDetail — right-side panel showing the selected day with its items.
 * Owners can edit title/notes and add/remove items; followers can tick the day off.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import LibraryPicker from './LibraryPicker';
import UploadDialog from './UploadDialog';

function ItemRow({ item, isOwner, onRemove }) {
  const isPdf = item.kind === 'paper' || item.kind === 'upload';
  const flagged = item.filterStatus === 'flagged';
  if (isPdf) {
    return (
      <li className={`cal-item-row${flagged ? ' flagged' : ''}`}>
        <Link to={`/viewer/${item.paperId}`} className="cal-item-main" target="_blank" rel="noreferrer">
          <span className="cal-item-icon" aria-hidden>📄</span>
          <span className="cal-item-label">{item.label || item.paper?.title || 'Document'}</span>
        </Link>
        {flagged && <span className="cal-item-badge" title="Flagged for review — only visible to you">Flagged</span>}
        {isOwner && <button type="button" className="cal-icon-btn" onClick={onRemove} aria-label="Remove">×</button>}
      </li>
    );
  }
  if (item.kind === 'link') {
    return (
      <li className="cal-item-row">
        <a href={item.externalUrl} target="_blank" rel="noreferrer" className="cal-item-main">
          <span className="cal-item-icon" aria-hidden>🔗</span>
          <span className="cal-item-label">{item.label || item.externalUrl}</span>
        </a>
        {isOwner && <button type="button" className="cal-icon-btn" onClick={onRemove} aria-label="Remove">×</button>}
      </li>
    );
  }
  return (
    <li className="cal-item-row cal-item-note">
      <span className="cal-item-icon" aria-hidden>📝</span>
      <span className="cal-item-label">{item.label}</span>
      {isOwner && <button type="button" className="cal-icon-btn" onClick={onRemove} aria-label="Remove">×</button>}
    </li>
  );
}

export default function DayDetail({
  day, isOwner, isFollowing, canTick,
  onUpdateDay, onDeleteDay, onAddItem, onRemoveItem, onToggleDone, calendarId,
}) {
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(day.title || '');
  const [draftNotes, setDraftNotes] = useState(day.notes || '');
  const [adderOpen, setAdderOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [linkInput, setLinkInput] = useState('');
  const [noteInput, setNoteInput] = useState('');
  const [warning, setWarning] = useState('');

  function startEdit() {
    setDraftTitle(day.title || '');
    setDraftNotes(day.notes || '');
    setEditing(true);
  }

  async function saveEdit() {
    await onUpdateDay({ title: draftTitle, notes: draftNotes });
    setEditing(false);
  }

  const dateObj = new Date(day.eventDate + 'T00:00:00');
  const longDate = dateObj.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <aside className="cal-day-detail">
      <header className="cal-day-detail-head">
        <div>
          <div className="cal-day-detail-date">{longDate}</div>
          {editing ? (
            <input
              className="cal-day-detail-title-input"
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              placeholder="Day title (optional)"
              maxLength={200}
            />
          ) : (
            <h2 className="cal-day-detail-title">{day.title || 'Untitled day'}</h2>
          )}
        </div>
        <div className="cal-day-detail-actions">
          {canTick && (
            <label className="cal-day-tick">
              <input
                type="checkbox"
                checked={!!day.completed}
                onChange={(e) => onToggleDone(e.target.checked)}
              />
              <span>Done</span>
            </label>
          )}
          {isOwner && !editing && <button className="secondary-btn" onClick={startEdit}>Edit</button>}
          {isOwner && editing && (
            <>
              <button className="primary-btn" onClick={saveEdit}>Save</button>
              <button className="secondary-btn" onClick={() => setEditing(false)}>Cancel</button>
            </>
          )}
        </div>
      </header>

      {editing ? (
        <textarea
          className="cal-day-detail-notes-input"
          value={draftNotes}
          onChange={(e) => setDraftNotes(e.target.value)}
          rows={4}
          placeholder="Notes for this day…"
          maxLength={4000}
        />
      ) : day.notes ? (
        <p className="cal-day-detail-notes">{day.notes}</p>
      ) : null}

      {warning && <div className="tt-status saving">{warning}</div>}

      <ul className="cal-item-list">
        {day.items.map((it) => (
          <ItemRow
            key={it.id}
            item={it}
            isOwner={isOwner}
            onRemove={() => onRemoveItem(it)}
          />
        ))}
        {day.items.length === 0 && (
          <li className="cal-item-empty">No items yet{isOwner ? ' — add a paper, upload, link or note.' : '.'}</li>
        )}
      </ul>

      {isOwner && (
        <div className="cal-day-adder">
          {!adderOpen ? (
            <button className="primary-btn" onClick={() => setAdderOpen(true)}>+ Add item</button>
          ) : (
            <div className="cal-day-adder-menu">
              <div className="cal-adder-row">
                <button type="button" className="cal-adder-btn" onClick={() => { setAdderOpen(false); setPickerOpen(true); }}>📚 From library</button>
                <button type="button" className="cal-adder-btn" onClick={() => { setAdderOpen(false); setUploadOpen(true); }}>⬆️ Upload PDF</button>
              </div>
              <form className="cal-adder-inline" onSubmit={async (e) => {
                e.preventDefault();
                if (!/^https?:\/\//i.test(linkInput.trim())) return;
                await onAddItem({ kind: 'link', externalUrl: linkInput.trim(), label: linkInput.trim() });
                setLinkInput('');
                setAdderOpen(false);
              }}>
                <input
                  type="url"
                  placeholder="🔗 Paste a link (https://…)"
                  value={linkInput}
                  onChange={(e) => setLinkInput(e.target.value)}
                />
                <button type="submit" className="secondary-btn" disabled={!linkInput.trim()}>Add link</button>
              </form>
              <form className="cal-adder-inline" onSubmit={async (e) => {
                e.preventDefault();
                if (!noteInput.trim()) return;
                await onAddItem({ kind: 'note', label: noteInput.trim() });
                setNoteInput('');
                setAdderOpen(false);
              }}>
                <input
                  type="text"
                  placeholder="📝 Quick note"
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                  maxLength={300}
                />
                <button type="submit" className="secondary-btn" disabled={!noteInput.trim()}>Add note</button>
              </form>
              <button type="button" className="cal-link-btn" onClick={() => setAdderOpen(false)}>Cancel</button>
            </div>
          )}
        </div>
      )}

      {isOwner && (
        <div className="cal-day-detail-foot">
          <button type="button" className="cal-link-btn cal-link-danger" onClick={onDeleteDay}>
            Remove this day
          </button>
        </div>
      )}

      {pickerOpen && (
        <LibraryPicker
          existingPaperIds={(day.items || []).filter((i) => i.paperId).map((i) => i.paperId)}
          onClose={() => setPickerOpen(false)}
          onPick={async (data) => {
            await onAddItem(data);
          }}
        />
      )}
      {uploadOpen && (
        <UploadDialog
          calendarId={calendarId}
          dayId={day.id}
          onClose={() => setUploadOpen(false)}
          onUploaded={(item, warn) => {
            if (warn) setWarning(`Uploaded but flagged: ${warn}`);
            onAddItem(null, item); // pass-through item, see CalendarView handler
          }}
        />
      )}
    </aside>
  );
}
